import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Form, Modal, Table } from 'react-bootstrap';
import CurrencyApi from '../../../api/CurrencyApi';
import CurrencyMasterApi from '../../../api/CurrencyMasterApi';
import SystemSettingsApi from '../../../api/SystemSettingsApi';
import { clearMultiCurrencySettingsCache } from '../../../utils/multiCurrencySettings';

const defaultForm = {
  currency_code: '',
  is_default: false,
  is_base_currency: false,
  is_active: true,
  exchange_rate: 1,
  decimal_places: 2,
};

const parseBoolean = (value) => {
  if (typeof value === 'boolean') return value;
  return String(value).toLowerCase() === 'true';
};

const CurrencyManager = () => {
  const api = useMemo(() => new CurrencyApi(), []);
  const masterApi = useMemo(() => new CurrencyMasterApi(), []);
  const settingsApi = useMemo(() => new SystemSettingsApi(), []);
  const [list, setList] = useState([]);
  const [masterList, setMasterList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [saving, setSaving] = useState(false);

  const [multiCurrencyEnabled, setMultiCurrencyEnabled] = useState(null);
  const [settingsList, setSettingsList] = useState([]);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [enablingMultiCurrency, setEnablingMultiCurrency] = useState(false);

  const availableCurrenciesForAdd = useMemo(() => {
    const existingCodes = new Set((list || []).map((r) => (r.currency_code || '').toUpperCase()));
    return (masterList || []).filter(
      (m) => m.currency_code && !existingCodes.has(String(m.currency_code).toUpperCase())
    );
  }, [masterList, list]);

  const hasBaseCurrency = useMemo(() => list.some((r) => r.is_base_currency === true), [list]);

  useEffect(() => {
    const load = async () => {
      setSettingsLoading(true);
      try {
        const res = await settingsApi.fetchSettings();
        const data = res?.data;
        if (data?.success && Array.isArray(data.data)) {
          setSettingsList(data.data);
          const item = data.data.find((s) => s && s.key === 'multi_currency_enabled');
          setMultiCurrencyEnabled(item ? parseBoolean(item.value) : false);
        } else {
          setMultiCurrencyEnabled(false);
        }
      } catch {
        setMultiCurrencyEnabled(false);
      } finally {
        setSettingsLoading(false);
      }
    };
    load();
  }, [settingsApi]);

  const handleEnableMultiCurrency = async () => {
    setEnablingMultiCurrency(true);
    setMessage('');
    try {
      const base = (settingsList || []).map((s) => ({
        key: s.key,
        label: s.label || s.key,
        value: s.key === 'multi_currency_enabled' ? 'true' : (s.value ?? ''),
        config: s.config || {},
      }));
      const hasMultiKey = base.some((s) => s && s.key === 'multi_currency_enabled');
      const updated = hasMultiKey
        ? base
        : [
          ...base,
          {
            key: 'multi_currency_enabled',
            label: 'Multi Currency Enabled',
            value: 'true',
            config: { datatype: 'checkbox' },
          },
        ];
      const res = await settingsApi.updateSettings(updated);
      if (res?.data?.success) {
        setMultiCurrencyEnabled(true);
        clearMultiCurrencySettingsCache();
        const nextSettings = res.data?.data;
        if (Array.isArray(nextSettings)) setSettingsList(nextSettings);
      } else {
        setMessage(res?.data?.message || 'Failed to enable Multi Currency.');
      }
    } catch (err) {
      setMessage(err?.response?.data?.message || err?.message || 'Failed to enable Multi Currency.');
    } finally {
      setEnablingMultiCurrency(false);
    }
  };

  const loadList = useCallback(async () => {
    setLoading(true);
    setMessage('');
    try {
      const res = await api.fetchAll();
      const data = res?.data;
      if (data?.success) {
        setList(data.data || []);
      } else {
        setMessage(data?.message || 'Failed to load currencies');
      }
    } catch (err) {
      setMessage(err?.response?.data?.message || err?.message || 'Failed to load currencies');
      setList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMasterList = useCallback(async () => {
    try {
      const res = await masterApi.fetchAll();
      const data = res?.data;
      if (data?.success && Array.isArray(data.data)) {
        setMasterList(data.data);
      }
    } catch {
      setMasterList([]);
    }
  }, [masterApi]);

  useEffect(() => {
    if (multiCurrencyEnabled === true) {
      loadList();
      loadMasterList();
    }
  }, [loadList, loadMasterList, multiCurrencyEnabled]);

  const openCreate = () => {
    setEditingId(null);
    setForm(defaultForm);
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      currency_code: row.currency_code || '',
      is_default: Boolean(row.is_default),
      is_base_currency: Boolean(row.is_base_currency),
      is_active: Boolean(row.is_active),
      exchange_rate: Number(row.exchange_rate) ?? 1,
      decimal_places: Number(row.decimal_places) ?? 2,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setForm(defaultForm);
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editingId && !form.currency_code?.trim()) {
      setMessage('Please select a currency.');
      return;
    }
    setSaving(true);
    setMessage('');
    try {
      if (editingId) {
        const payload = {
          exchange_rate: form.exchange_rate,
          decimal_places: form.decimal_places,
          is_default: form.is_default,
          is_base_currency: form.is_base_currency,
          is_active: form.is_active,
        };
        const res = await api.update(payload, editingId);
        if (res?.data?.success) {
          closeModal();
          loadList();
        } else {
          setMessage(res?.data?.message || 'Update failed');
        }
      } else {
        const payload = {
          currency_code: form.currency_code.trim(),
          exchange_rate: form.exchange_rate,
          decimal_places: form.decimal_places,
          is_default: form.is_default,
          is_base_currency: form.is_base_currency && !hasBaseCurrency,
          is_active: form.is_active,
        };
        const res = await api.create(payload);
        if (res?.data?.success) {
          closeModal();
          loadList();
        } else {
          setMessage(res?.data?.message || 'Create failed');
        }
      }
    } catch (err) {
      setMessage(err?.response?.data?.message || err?.message || 'Request failed');
    } finally {
      setSaving(false);
    }
  };

  if (settingsLoading) {
    return (
      <div className="wrapper">
        <div id="content">
          <Card className="p-4">
            <p className="text-muted mb-0">Loading...</p>
          </Card>
        </div>
      </div>
    );
  }

  if (multiCurrencyEnabled === false) {
    return (
      <div className="wrapper">
        <div id="content">
          <Card className="p-4">
            <h4 className="mb-3">Manage Exchange Rate</h4>
            <Alert variant="info" className="mb-3">
              Multi Currency is currently disabled. Enable it to add and manage exchange rates.
            </Alert>
            <Alert variant="warning" className="mb-4">
              <strong>Note:</strong> Once enabled, Multi Currency cannot be disabled. Please confirm before enabling.
            </Alert>
            {message && (
              <div className="alert alert-danger mb-3" role="alert">
                {message}
              </div>
            )}
            <Button
              variant="primary"
              onClick={handleEnableMultiCurrency}
              disabled={enablingMultiCurrency}
            >
              {enablingMultiCurrency ? 'Enabling...' : 'Enable Multi Currency'}
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="wrapper">
      <div id="content">
        <Card className="p-4">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h4 className="mb-0">Manage Exchange Rate</h4>
            <Button variant="primary" onClick={openCreate} disabled={availableCurrenciesForAdd.length === 0}>
              Add Exchange Rate
            </Button>
          </div>
          {message && (
            <div className="alert alert-info mb-3" role="alert">
              {message}
            </div>
          )}
          {loading ? (
            <p className="text-muted">Loading...</p>
          ) : (
            <Table responsive bordered hover>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Exchange Rate</th>
                  <th>Decimals</th>
                  <th>Base</th>
                  <th>Active</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-muted text-center py-4">
                      No exchange rates yet. Add currencies in Setup &gt; Currency &gt; Manage Currency, then add an exchange rate here.
                    </td>
                  </tr>
                ) : (
                  list.map((row) => (
                    <tr key={row.id}>
                      <td>{row.currency_code}</td>
                      <td>{row.currency_name}</td>
                      <td>{Number(row.exchange_rate)}</td>
                      <td>{Number(row.decimal_places)}</td>
                      <td>{row.is_base_currency ? 'Yes' : '—'}</td>
                      <td>{row.is_active ? 'Yes' : 'No'}</td>
                      <td className="text-end">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => openEdit(row)}
                        >
                          Edit
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          )}
        </Card>

        <Modal show={showModal} onHide={closeModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>{editingId ? 'Edit Exchange Rate' : 'Add Exchange Rate'}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              {editingId ? (
                <Form.Group className="mb-3">
                  <Form.Label>Currency</Form.Label>
                  <Form.Control
                    type="text"
                    value={
                      (() => {
                        const row = list.find((r) => r.id === editingId);
                        if (!row) return form.currency_code || '';
                        return [row.currency_code, row.currency_name].filter(Boolean).join(' ');
                      })()
                    }
                    readOnly
                    plaintext
                  />
                </Form.Group>
              ) : (
                <Form.Group className="mb-3">
                  <Form.Label>Currency *</Form.Label>
                  <Form.Select
                    value={form.currency_code}
                    onChange={(e) => handleChange('currency_code', e.target.value)}
                    required
                  >
                    <option value="">Select currency</option>
                    {availableCurrenciesForAdd.map((m) => (
                      <option key={m.currency_code} value={m.currency_code}>
                        {m.currency_code} – {m.currency_name || m.currency_code}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Add currencies in Manage Currency first if needed.
                  </Form.Text>
                </Form.Group>
              )}
              <Form.Group className="mb-3">
                <Form.Label>Exchange Rate</Form.Label>
                <Form.Control
                  type="number"
                  step="0.000001"
                  min="0"
                  value={form.exchange_rate}
                  onChange={(e) => handleChange('exchange_rate', e.target.value)}
                />
                <Form.Text className="text-muted">Rate to base (default) currency. Base = 1.</Form.Text>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Decimal Places</Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  max={4}
                  value={form.decimal_places}
                  onChange={(e) => handleChange('decimal_places', e.target.value)}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Check
                  type="switch"
                  id="is_base_currency"
                  label="Set as base currency"
                  checked={form.is_base_currency}
                  onChange={(e) => handleChange('is_base_currency', e.target.checked)}
                  disabled={editingId ? (list.find((r) => r.id === editingId)?.is_base_currency === true) : hasBaseCurrency}
                />
                {(editingId && list.find((r) => r.id === editingId)?.is_base_currency === true) && (
                  <Form.Text className="text-muted d-block mt-1">
                    Base currency cannot be changed once set for this org.
                  </Form.Text>
                )}
                {!editingId && hasBaseCurrency && (
                  <Form.Text className="text-muted d-block mt-1">
                    Base currency is already set and cannot be changed.
                  </Form.Text>
                )}
              </Form.Group>
              <Form.Check
                type="switch"
                id="is_active"
                label="Active"
                checked={form.is_active}
                onChange={(e) => handleChange('is_active', e.target.checked)}
              />
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={closeModal} type="button">
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={saving}>
                {saving ? 'Saving...' : editingId ? 'Update' : 'Add'}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </div>
    </div>
  );
};

export default CurrencyManager;
